import { useState, useEffect } from "react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { XIcon } from "lucide-react"

interface CargoType {
  id: string
  name: string
}

interface FormState {
  name: string
  code: string
  cargoTypeId: string
}

const EMPTY_FORM: FormState = {
  name: "",
  code: "",
  cargoTypeId: "",
}

interface Props {
  open: boolean
  onClose: () => void
  editing: { id: string; name: string; code: string; cargoTypeId: string | null; sgtCodes: string[] | null } | null
  cargoTypes: CargoType[]
  onSave: (id: string | null, data: { name: string; code: string; cargoTypeId: string | null; sgtCodes: string[] | null }) => void
  isSaving: boolean
  error: string | null
}

export function CargoProductFormSheet({ open, onClose, editing, cargoTypes, onSave, isSaving, error }: Props) {
  const [form, setForm]         = useState<FormState>(EMPTY_FORM)
  const [sgtCodes, setSgtCodes] = useState<string[]>([])
  const [sgtInput, setSgtInput] = useState("")

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name,
        code: editing.code,
        cargoTypeId: editing.cargoTypeId ?? "",
      })
      setSgtCodes(editing.sgtCodes ?? [])
    } else {
      setForm(EMPTY_FORM)
      setSgtCodes([])
    }
    setSgtInput("")
  }, [editing, open])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSave(editing?.id ?? null, {
      ...form,
      cargoTypeId: form.cargoTypeId || null,
      sgtCodes: sgtCodes.length > 0 ? sgtCodes : null,
    })
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="sm:max-w-sm overflow-y-auto flex flex-col">
        <SheetHeader>
          <SheetTitle>{editing ? "Editar producto" : "Nuevo producto"}</SheetTitle>
          <SheetDescription>Mantené el código alineado al catálogo logístico.</SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">
          <div className="grid gap-1.5">
            <Label htmlFor="cargo-code">Código</Label>
            <Input
              id="cargo-code"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              maxLength={10}
              className="uppercase font-mono"
              placeholder="ACEITE"
              required
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="cargo-name">Nombre</Label>
            <Input
              id="cargo-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              maxLength={200}
              placeholder="Aceite"
              required
            />
          </div>

          {/* Códigos SGT */}
          <div className="grid gap-1.5">
            <Label>Códigos SGT</Label>
            <div className="flex flex-wrap gap-1.5 items-center min-h-9 rounded-md border border-input px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
              {sgtCodes.map((code, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-muted font-mono text-xs px-2 py-0.5 rounded-sm">
                  {code}
                  <button type="button" onClick={() => setSgtCodes(sgtCodes.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground">
                    <XIcon className="size-3" />
                  </button>
                </span>
              ))}
              <input
                value={sgtInput}
                onChange={(e) => setSgtInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault()
                    const code = sgtInput.toUpperCase().trim()
                    if (code && !sgtCodes.includes(code)) setSgtCodes([...sgtCodes, code])
                    setSgtInput("")
                  }
                }}
                placeholder={sgtCodes.length === 0 ? "Escribir y Enter para agregar..." : ""}
                className="flex-1 min-w-28 bg-transparent text-xs font-mono outline-none placeholder:text-muted-foreground"
              />
            </div>
            <p className="text-xs text-muted-foreground">Nombres exactos en SmartGT. Se normalizan automáticamente.</p>
          </div>

          {/* Tipo de carga */}
          <div className="grid gap-1.5">
            <Label>Tipo de carga</Label>
            <Select
              value={form.cargoTypeId || "__none__"}
              onValueChange={(v) => setForm((f) => ({ ...f, cargoTypeId: v === "__none__" ? "" : (v ?? "") }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sin tipo">
                  {form.cargoTypeId
                    ? (cargoTypes.find((t) => t.id === form.cargoTypeId)?.name ?? form.cargoTypeId)
                    : null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin tipo</SelectItem>
                {cargoTypes.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</p>
          )}

          <SheetFooter className="px-0 mt-auto pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear producto"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
